import React, { useState, useEffect, useCallback } from 'react'
import {
  Box,
  Typography,
  Paper,
  Divider,
  TextField,
  Button,
  Alert,
  Breadcrumbs,
  Link,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Tooltip,
  CircularProgress,
  Checkbox,
  FormControlLabel,
} from '@mui/material'
import { colorTokens } from '@/apps/legacy/shared/styles/colorTokens'
import { useNavigate, useParams } from 'react-router-dom'
import { PortalLayout } from '@/apps/legacy/components/portal/PortalLayout'
import SoapTemplateSection from '@/apps/legacy/components/portal/SoapTemplateSection'
import AdvancedSearchBox from '@/apps/legacy/components/portal/AdvancedSearchBox'
import { AdvancedSearchCondition } from '@/shared/types/advancedSearch'
import {
  useDrugSearchQuery,
  useDrugGroupDetailQuery,
  useUpdateDrugGroupMutation,
  useDeleteDrugGroupMutation,
  useDrugGroupDrugs,
} from '@/features/internal/drugs'
import {
  useGroupSoapTemplatesQuery,
  useUpdateGroupSoapTemplatesMutation,
  useKeywordSuggestionMutation,
  getKeywordSuggestionFallback,
} from '@/features/internal/drugs'
import {
  SoapTemplateGroup,
  SoapType,
  Drug,
  KeywordSuggestionResponse,
} from '@/features/internal/drugs/types/soapTemplate'
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  DeleteOutline as DeleteOutlineIcon,
} from '@mui/icons-material'
import {
  ActionButton,
  EditButton,
  BackButton,
  CancelButton,
} from '@/apps/legacy/components/portal/CustomButton'
import {
  SECONDARY_DELETE_BUTTON_STYLE,
  SECONDARY_ICON_BUTTON_STYLE,
} from '@/shared/constants/buttonStyles'
import { LoadingIndicator } from '@/apps/legacy/components/atoms/LoadingIndicator'

/**
 * 医薬品グループ詳細・編集画面
 */
const DrugGroupDetailPage: React.FC = () => {
  // URLからグループIDを取得
  const { id: groupId = '' } = useParams<{ id: string }>()
  const navigate = useNavigate()

  // 'create'パスの場合のフラグ（hooksは常に呼び出す必要があるため、returnは後で行う）
  const isCreatePath = groupId === 'create'

  // 医薬品グループ詳細クエリ（TanStack Query）
  // create パスの場合は無効化
  const {
    data: selectedGroup,
    isLoading: groupLoading,
    error: groupError,
  } = useDrugGroupDetailQuery(isCreatePath ? undefined : groupId)

  // 更新・削除 Mutation
  const updateGroupMutation = useUpdateDrugGroupMutation()
  const deleteGroupMutation = useDeleteDrugGroupMutation()

  // create パスの場合は空のグループIDを渡す（hooksは常に呼び出す必要がある）
  const {
    loading: drugsLoading,
    error: drugsError,
    groupDrugs,
    selectedDrugIds,
    removeDrugFromGroupAction,
    bulkDeleteDrugsFromGroupAction,
    toggleDrugSelection,
    selectAllDrugs,
    deselectAllDrugs,
    clearSelection,
  } = useDrugGroupDrugs(isCreatePath ? '' : groupId)

  // テンプレート関連のフック（TanStack Query）
  // create パスの場合は無効化
  const {
    templateGroups,
    isLoading: templatesLoading,
    error: templatesError,
  } = useGroupSoapTemplatesQuery(isCreatePath ? undefined : groupId)

  // 更新 Mutation
  const updateTemplatesMutation = useUpdateGroupSoapTemplatesMutation()

  // キーワード提案 Mutation
  const keywordSuggestionMutation = useKeywordSuggestionMutation()

  // 状態管理
  const [editMode, setEditMode] = useState(false)
  const [groupName, setGroupName] = useState(() => selectedGroup?.name || '')
  const [groupDescription, setGroupDescription] = useState(
    () => selectedGroup?.description || ''
  )
  const [nameError, setNameError] = useState('')
  const [soapTemplates, setSoapTemplates] = useState<SoapTemplateGroup[]>(
    () => templateGroups || []
  )
  const [saveSuccess, setSaveSuccess] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [deleteSuccess, setDeleteSuccess] = useState(false)
  const [emptyItemsWarning, setEmptyItemsWarning] = useState<{
    show: boolean
    items: { groupName: string; soapType: string }[]
  }>({
    show: false,
    items: [],
  })
  const [drugSearchTerm, setDrugSearchTerm] = useState('')
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false)

  // 高度検索フック（所属医薬品検索用 - TanStack Query）
  const {
    normalSearchKeyword: advancedSearchKeyword,
    setNormalSearchKeyword: setAdvancedSearchKeyword,
    advancedConditions,
    searchResults: advancedSearchResults,
    isLoading: advancedSearchLoading,
    error: advancedSearchError,
    executeNormalSearch: executeAdvancedNormalSearch,
    executeAdvancedSearch,
    isAdvancedMode,
    setIsAdvancedMode,
  } = useDrugSearchQuery()

  // 高度検索を使用するかどうかのフラグ
  const [useAdvancedSearchForFilter, setUseAdvancedSearchForFilter] =
    useState(false)

  // グループ詳細データが取得されたらフォームを初期化
  // 初期化は useState の lazy initialization で行うため、useEffect は不要

  // 所属医薬品はuseDrugGroupDrugs(groupId)でgroupId変更時に自動取得

  // テンプレートデータが取得されたら編集用状態を初期化
  // useEffectは削除 - useState の lazy initialization とキャンセルハンドラーで管理

  // 成功メッセージのタイムアウト管理
  useEffect(() => {
    let timeoutId: NodeJS.Timeout

    if (saveSuccess) {
      timeoutId = setTimeout(() => {
        setSaveSuccess(false)
      }, 3000)
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId)
      }
    }
  }, [saveSuccess])

  // 削除成功後のリダイレクト管理
  useEffect(() => {
    let timeoutId: NodeJS.Timeout

    if (deleteSuccess) {
      timeoutId = setTimeout(() => {
        navigate('/portal/soaps?tab=groups')
      }, 3000)
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId)
      }
    }
  }, [deleteSuccess, navigate])

  // 編集モード切替
  const toggleEditMode = () => {
    const newEditMode = !editMode
    setEditMode(newEditMode)
    // 編集モードを終了する場合は警告をクリア
    if (!newEditMode) {
      setEmptyItemsWarning({
        show: false,
        items: [],
      })
    }
  }

  // フォーム検証
  const validateForm = () => {
    let isValid = true

    if (!groupName.trim()) {
      setNameError('グループ名は必須です')
      isValid = false
    } else {
      setNameError('')
    }

    return isValid
  }

  // 空のSOAP要素をチェックする関数
  const validateEmptyItems = () => {
    const emptyItems: { groupName: string; soapType: string }[] = []

    // 全てのテンプレートグループをチェック
    soapTemplates.forEach((group) => {
      // 各グループ内のSOAP項目をチェック
      group.templates.forEach((item) => {
        // contentが空の場合は問題ありとマーク
        if (!item.content.trim()) {
          emptyItems.push({
            groupName: group.name,
            soapType: item.soap_type,
          })
        }
      })
    })

    return emptyItems
  }

  // 保存処理
  const handleSave = async () => {
    if (!validateForm()) return

    // 空のSOAP要素をチェック
    const emptyItems = validateEmptyItems()

    // 空の要素がある場合は警告を表示して処理中断
    if (emptyItems.length > 0) {
      setEmptyItemsWarning({
        show: true,
        items: emptyItems,
      })
      // 成功メッセージが表示されないようにリセット
      setSaveSuccess(false)
      return
    }

    // 警告表示をクリア
    setEmptyItemsWarning({
      show: false,
      items: [],
    })

    try {
      // グループ更新（Mutation使用）
      await updateGroupMutation.mutateAsync({
        groupId,
        data: {
          name: groupName,
          description: groupDescription,
        },
      })

      // SOAPテンプレート更新（Mutation使用）
      await updateTemplatesMutation.mutateAsync({
        groupId,
        templateGroups: soapTemplates,
      })

      // 両方成功した場合のみ成功メッセージを表示
      setSaveSuccess(true)
      setEditMode(false)
    } catch (_err) {
      setSaveSuccess(false)
    }
  }

  // キャンセル処理
  const handleCancel = () => {
    if (window.confirm('編集内容が破棄されますが、よろしいですか？')) {
      // グループ情報を元に戻す
      if (selectedGroup) {
        setGroupName(selectedGroup.name)
        setGroupDescription(selectedGroup.description)
      }

      // テンプレートを元に戻す（サーバーデータから復元）
      setSoapTemplates([...templateGroups])

      setEditMode(false)

      // 警告をクリア
      setEmptyItemsWarning({
        show: false,
        items: [],
      })
    }
  }

  // 削除ダイアログを開く
  const openDeleteDialog = () => {
    setDeleteDialogOpen(true)
  }

  // 削除処理を実行（Mutation使用）
  const handleDelete = async () => {
    try {
      // 削除前にエラー状態をリセット
      setDeleteOperationError(false)
      await deleteGroupMutation.mutateAsync(groupId)

      setDeleteDialogOpen(false)
      setDeleteSuccess(true)

      // 削除成功後、3秒後に一覧画面へ戻る
      setTimeout(() => {
        navigate('/portal/soaps?tab=groups')
      }, 3000)
    } catch (_err) {
      setDeleteDialogOpen(false)
      setDeleteOperationError(true)
    }
  }

  // 削除ダイアログをキャンセル
  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false)
  }

  // テンプレートグループ名変更ハンドラ
  const handleGroupNameChange = useCallback(
    (groupId: string, name: string) => {
      if (editMode) {
        setSoapTemplates((prevTemplates) =>
          prevTemplates.map((group) =>
            group.id === groupId ? { ...group, name } : group
          )
        )
      }
    },
    [editMode]
  )

  // SOAP項目追加ハンドラ
  const handleAddItem = useCallback(
    (groupId: string, soapType: SoapType) => {
      if (editMode) {
        setSoapTemplates((prevTemplates) =>
          prevTemplates.map((group) => {
            if (group.id === groupId) {
              return {
                ...group,
                templates: [
                  ...group.templates,
                  {
                    id: `new-item-${Date.now()}`,
                    soap_type: soapType,
                    content: '',
                  },
                ],
              }
            }
            return group
          })
        )
      }
    },
    [editMode]
  )

  // SOAP項目削除ハンドラ
  const handleDeleteItem = useCallback(
    (groupId: string, itemId: string) => {
      if (editMode) {
        setSoapTemplates((prevTemplates) =>
          prevTemplates.map((group) => {
            if (group.id === groupId) {
              return {
                ...group,
                templates: group.templates.filter((item) => item.id !== itemId),
              }
            }
            return group
          })
        )
      }
    },
    [editMode]
  )

  // キーワード変更ハンドラ
  const handleKeywordsChange = useCallback(
    (groupId: string, keywords: string[]) => {
      if (editMode) {
        setSoapTemplates((prevTemplates) =>
          prevTemplates.map((group) =>
            group.id === groupId ? { ...group, keywords } : group
          )
        )
      }
    },
    [editMode]
  )

  // SOAP項目内容変更ハンドラ
  const handleItemContentChange = useCallback(
    (groupId: string, itemId: string, content: string) => {
      if (editMode) {
        setSoapTemplates((prevTemplates) =>
          prevTemplates.map((group) => {
            if (group.id === groupId) {
              return {
                ...group,
                templates: group.templates.map((item) =>
                  item.id === itemId ? { ...item, content } : item
                ),
              }
            }
            return group
          })
        )
      }
    },
    [editMode]
  )

  // グループ削除ハンドラ
  const handleDeleteGroup = useCallback(
    (groupId: string) => {
      if (editMode) {
        setSoapTemplates((prevTemplates) =>
          prevTemplates.filter((group) => group.id !== groupId)
        )
      }
    },
    [editMode]
  )

  // キーワード提案ハンドラ（TanStack Query Mutation使用）
  const handleSuggestKeywords = useCallback(
    async (
      targetGroupId: string,
      _templateContent: string,
      _existingKeywords: string[]
    ): Promise<KeywordSuggestionResponse> => {
      try {
        const result = await keywordSuggestionMutation.mutateAsync(
          targetGroupId
        )
        return { success: true, data: result }
      } catch (_err) {
        // エラー時はフォールバックデータを返してユーザー体験を保持
        return { success: false, data: getKeywordSuggestionFallback() }
      }
    },
    [keywordSuggestionMutation]
  )

  // 医薬品追加ページへの遷移
  const handleAddDrugs = () => {
    navigate(`/portal/soaps/groups/${groupId}/drugs`)
  }

  // 一覧へ戻る
  const handleBackToList = () => {
    navigate('/portal/soaps?tab=groups')
  }

  // ローディング状態（医薬品検索は displayData で管理）
  const isLoading =
    groupLoading ||
    templatesLoading ||
    updateGroupMutation.isPending ||
    deleteGroupMutation.isPending

  // エラーハンドリング用の状態変数（削除操作のエラーは別管理）
  const [deleteOperationError, setDeleteOperationError] =
    useState<boolean>(false)
  // 表示用エラー状態（医薬品検索エラーは displayData で管理）
  const hasError = groupError || templatesError

  // 所属する医薬品を削除するハンドラ
  const handleRemoveDrug = async (drugId: string) => {
    if (window.confirm('この医薬品をグループから削除してよろしいですか？')) {
      try {
        const success = await removeDrugFromGroupAction(groupId, drugId)
        if (!success) {
          // エラーハンドリングは上位レイヤーで行われるため、ここでは何もしない
        }
      } catch (_err) {
        // エラーハンドリングは上位レイヤーで行われるため、ここでは何もしない
      }
    }
  }

  // 選択された医薬品を一括削除
  const handleBulkDelete = async () => {
    setBulkDeleteDialogOpen(false)
    try {
      const success = await bulkDeleteDrugsFromGroupAction(
        groupId,
        selectedDrugIds
      )
      if (success) {
        clearSelection()
        setDrugSearchTerm('') // 検索をリセット
        setUseAdvancedSearchForFilter(false) // 高度検索もリセット
        setAdvancedSearchKeyword('')
        setIsAdvancedMode(false) // 高度検索モードもリセット
      }
    } catch (_err) {
      // エラーハンドリングは上位レイヤーで行われるため、ここでは何もしない
    }
  }

  // 統一された表示データインターフェース（医薬品検索用）
  const displayData = useAdvancedSearchForFilter
    ? {
        drugs: advancedSearchResults
          ? groupDrugs.filter((drug) =>
              advancedSearchResults.results.some(
                (result) => result.id === drug.id
              )
            )
          : [],
        loading: advancedSearchLoading,
        error: advancedSearchError?.message ?? null,
        hasSearch: advancedConditions.length > 0,
      }
    : {
        drugs: !drugSearchTerm
          ? groupDrugs
          : groupDrugs.filter((drug) => {
              const searchLower = drugSearchTerm.toLowerCase()
              return (
                drug.yj_code.toLowerCase().includes(searchLower) ||
                (drug.product_name &&
                  drug.product_name.toLowerCase().includes(searchLower)) ||
                (drug.general_name &&
                  drug.general_name.toLowerCase().includes(searchLower))
              )
            }),
        loading: drugsLoading,
        error: drugsError,
        hasSearch: !!drugSearchTerm,
      }

  // すべて選択/解除の処理
  const isAllSelected =
    displayData.drugs.length > 0 &&
    displayData.drugs.every((drug) => selectedDrugIds.includes(drug.id))

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      // displayData.drugs（GroupDrug[]）を Drug[] として扱うため型変換
      const drugsToSelect: Drug[] = displayData.drugs.map((drug) => ({
        ...drug,
        id: drug.id,
        yj_code: drug.yj_code,
        product_name: drug.product_name,
        base_product_name: drug.product_name || '',
        price: '',
        manufacturer: drug.manufacturer_name,
        general_name: drug.general_name || '',
        base_general_name: drug.general_name || '',
      }))
      selectAllDrugs(drugsToSelect)
    } else {
      const drugsToDeselect: Drug[] = displayData.drugs.map((drug) => ({
        ...drug,
        id: drug.id,
        yj_code: drug.yj_code,
        product_name: drug.product_name,
        base_product_name: drug.product_name || '',
        price: '',
        manufacturer: drug.manufacturer_name,
        general_name: drug.general_name || '',
        base_general_name: drug.general_name || '',
      }))
      deselectAllDrugs(drugsToDeselect)
    }
  }

  // 'create'パスの場合はレンダリングしない（create.tsxを優先する）
  // hooks は全て呼び出し済みなので、ここで早期リターンしても rules-of-hooks 違反にならない
  if (isCreatePath) {
    return null
  }

  return (
    <PortalLayout>
      <Paper elevation={2} sx={{ p: 3 }}>
        {/* パンくずリスト */}
        <Breadcrumbs sx={{ mb: 2 }}>
          <Link
            color="inherit"
            onClick={(e) => {
              e.preventDefault()
              navigate('/portal/soaps')
            }}
            style={{ cursor: 'pointer' }}
          >
            SOAPテンプレート管理
          </Link>
          <Typography color="text.primary">
            {groupName || '医薬品グループ詳細'}
          </Typography>
        </Breadcrumbs>

        {/* ヘッダー部分 */}
        <Box
          sx={{
            mb: 3,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant="h5" component="h1">
            医薬品グループ詳細
          </Typography>
          {!editMode && (
            <EditButton
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={toggleEditMode}
            >
              編集
            </EditButton>
          )}
        </Box>
        <Divider sx={{ mb: 3 }} />

        {/* ローディング表示 */}
        {isLoading && (
          <Box my={4}>
            <LoadingIndicator />
          </Box>
        )}

        {/* 成功メッセージ */}
        {saveSuccess && (
          <Alert severity="success" sx={{ mb: 3 }}>
            医薬品グループが正常に更新されました。
          </Alert>
        )}

        {/* 削除成功メッセージ */}
        {deleteSuccess && (
          <Alert severity="info" sx={{ mb: 3 }}>
            医薬品グループが削除されました。一覧画面へ戻ります...
          </Alert>
        )}

        {/* エラー表示 - 削除操作のエラーは別途表示 */}
        {hasError && !deleteSuccess && (
          <Alert severity="error" sx={{ mb: 3 }}>
            操作中にエラーが発生しました。しばらくしてから再度お試しください。
          </Alert>
        )}

        {/* 削除操作のエラー表示 */}
        {deleteOperationError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            医薬品グループの削除中にエラーが発生しました。再度お試しください。
          </Alert>
        )}

        {!isLoading && (
          <>
            {/* 基本情報フォーム/表示 */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" gutterBottom>
                基本情報
              </Typography>
              {editMode ? (
                <>
                  <TextField
                    fullWidth
                    label="グループ名"
                    value={groupName}
                    onChange={(e) => setGroupName(e.target.value)}
                    margin="normal"
                    error={!!nameError}
                    helperText={nameError}
                    required
                  />
                  <TextField
                    fullWidth
                    label="説明"
                    value={groupDescription}
                    onChange={(e) => setGroupDescription(e.target.value)}
                    margin="normal"
                    multiline
                    rows={3}
                  />
                </>
              ) : (
                <Box sx={{ ml: 2 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    <strong>グループ名:</strong> {groupName}
                  </Typography>
                  <Typography variant="body1">
                    <strong>説明:</strong> {groupDescription || '(説明なし)'}
                  </Typography>
                </Box>
              )}
            </Box>

            {/* 所属医薬品一覧 */}
            <Box sx={{ mb: 4 }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 2,
                }}
              >
                <Typography variant="h6">所属医薬品</Typography>
                {editMode && (
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    {selectedDrugIds.length > 0 && (
                      <Button
                        startIcon={<DeleteOutlineIcon />}
                        variant="outlined"
                        size="small"
                        color="error"
                        onClick={() => setBulkDeleteDialogOpen(true)}
                      >
                        選択した{selectedDrugIds.length}件を削除
                      </Button>
                    )}
                    <ActionButton
                      startIcon={<AddIcon />}
                      variant="contained"
                      size="small"
                      onClick={handleAddDrugs}
                    >
                      医薬品追加
                    </ActionButton>
                  </Box>
                )}
              </Box>
              {editMode && (
                <Box sx={{ mb: 2 }}>
                  <AdvancedSearchBox
                    normalSearchValue={
                      useAdvancedSearchForFilter
                        ? advancedSearchKeyword
                        : drugSearchTerm
                    }
                    onNormalSearchChange={
                      useAdvancedSearchForFilter
                        ? setAdvancedSearchKeyword
                        : setDrugSearchTerm
                    }
                    onNormalSearch={() => {
                      if (useAdvancedSearchForFilter) {
                        executeAdvancedNormalSearch()
                      }
                    }}
                    onAdvancedSearch={(
                      conditions: AdvancedSearchCondition[]
                    ) => {
                      executeAdvancedSearch(conditions)
                      setUseAdvancedSearchForFilter(true)
                    }}
                    isAdvancedMode={isAdvancedMode}
                    onAdvancedModeChange={setIsAdvancedMode}
                    placeholder="医薬品名やYJコードで検索..."
                    initialAdvancedConditions={advancedConditions}
                  />
                </Box>
              )}

              {/* 医薬品検索のローディング状態 */}
              {displayData.loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
                  <CircularProgress size={20} />
                </Box>
              )}

              {/* 医薬品検索のエラー表示 */}
              {displayData.error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  医薬品検索中にエラーが発生しました。
                </Alert>
              )}

              {/* 全て選択チェックボックス（編集モード時のみ） */}
              {editMode &&
                displayData.drugs &&
                displayData.drugs.length > 0 && (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'flex-end',
                      alignItems: 'center',
                      mb: 1,
                    }}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={isAllSelected}
                          indeterminate={
                            selectedDrugIds.length > 0 && !isAllSelected
                          }
                          onChange={(e) => handleSelectAll(e.target.checked)}
                          size="small"
                        />
                      }
                      label="全て選択"
                    />
                  </Box>
                )}

              <TableContainer
                component={Paper}
                sx={{
                  mb: 2,
                  maxHeight:
                    displayData.drugs && displayData.drugs.length > 20
                      ? '400px'
                      : 'none',
                  overflow:
                    displayData.drugs && displayData.drugs.length > 20
                      ? 'auto'
                      : 'visible',
                }}
              >
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      {editMode && (
                        <TableCell padding="checkbox">
                          {/* 個別選択用のチェックボックス列ヘッダー */}
                        </TableCell>
                      )}
                      <TableCell>YJコード</TableCell>
                      <TableCell>医薬品名</TableCell>
                      <TableCell>製造元</TableCell>
                      {editMode && <TableCell align="right"></TableCell>}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {displayData.drugs && displayData.drugs.length > 0 ? (
                      displayData.drugs.map((drug) => (
                        <TableRow key={drug.id}>
                          {editMode && (
                            <TableCell padding="checkbox">
                              <Checkbox
                                checked={selectedDrugIds.includes(drug.id)}
                                onChange={() => toggleDrugSelection(drug.id)}
                              />
                            </TableCell>
                          )}
                          <TableCell>{drug.yj_code}</TableCell>
                          <TableCell>
                            {drug.product_name ? (
                              drug.product_name
                            ) : (
                              <span style={{ color: '#666' }}>
                                【般】{drug.general_name}
                              </span>
                            )}
                          </TableCell>
                          <TableCell>{drug.manufacturer_name}</TableCell>
                          {editMode && (
                            <TableCell align="right">
                              <Tooltip title="削除" placement="top">
                                <IconButton
                                  size="small"
                                  onClick={() => handleRemoveDrug(drug.id)}
                                  sx={SECONDARY_ICON_BUTTON_STYLE}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          )}
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={editMode ? 5 : 3} align="center">
                          {displayData.hasSearch
                            ? '検索条件に一致する医薬品がありません'
                            : '所属する医薬品がありません'}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
            {/* SOAPテンプレート編集セクション */}
            <Box sx={{ mb: 4 }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 2,
                }}
              >
                <Typography variant="h6">SOAPテンプレート</Typography>
                {editMode && (
                  <ActionButton
                    variant="contained"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => {
                      setSoapTemplates([
                        ...soapTemplates,
                        {
                          id: `new-template-group-${Date.now()}`,
                          name: '新規テンプレート',
                          keywords: [],
                          templates: [
                            {
                              id: `new-item-${Date.now()}`,
                              soap_type: 'S' as SoapType,
                              content: '',
                            },
                          ],
                        },
                      ])
                    }}
                  >
                    新規テンプレートグループを追加
                  </ActionButton>
                )}
              </Box>

              {(() => {
                if (soapTemplates.length === 0) {
                  return (
                    <Paper
                      elevation={1}
                      sx={{
                        p: 3,
                        mb: 2,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 2,
                        backgroundColor: colorTokens.background.subtle,
                      }}
                    >
                      <Typography variant="body1" color="text.secondary">
                        SOAPテンプレートが存在しません
                      </Typography>
                      {editMode && (
                        <ActionButton
                          variant="contained"
                          onClick={() => {
                            setSoapTemplates([
                              {
                                id: `new-template-group-${Date.now()}`,
                                name: '新規テンプレート',
                                keywords: [],
                                templates: [
                                  {
                                    id: `new-item-${Date.now()}`,
                                    soap_type: 'S' as SoapType,
                                    content: '',
                                  },
                                ],
                              },
                            ])
                          }}
                        >
                          テンプレートグループを作成
                        </ActionButton>
                      )}
                    </Paper>
                  )
                } else {
                  return soapTemplates.map((group) => {
                    return (
                      <SoapTemplateSection
                        key={group.id}
                        group={group}
                        editMode={editMode}
                        drugGroupName={groupName}
                        onGroupNameChange={handleGroupNameChange}
                        onKeywordsChange={handleKeywordsChange}
                        onAddItem={handleAddItem}
                        onDeleteItem={handleDeleteItem}
                        onItemContentChange={handleItemContentChange}
                        onDeleteGroup={handleDeleteGroup}
                        onSuggestKeywords={handleSuggestKeywords}
                      />
                    )
                  })
                }
              })()}
            </Box>

            {/* 空SOAP要素警告メッセージ（ボタン直前に表示） */}
            {emptyItemsWarning.show && (
              <Alert severity="warning" sx={{ mb: 3 }}>
                <Typography variant="body1">空のSOAP項目があります</Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  以下のSOAP項目に内容が入力されていません。
                  項目に内容を入力するか、不要な項目は削除してください。
                </Typography>
                <Box component="ul" sx={{ mt: 1, pl: 2 }}>
                  {emptyItemsWarning.items.map((item, index) => (
                    <Box component="li" key={index}>
                      {item.groupName}：{item.soapType}項目
                    </Box>
                  ))}
                </Box>
              </Alert>
            )}

            {/* アクションボタン */}
            {editMode ? (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  mt: 4,
                }}
              >
                <Button
                  variant="outlined"
                  color="inherit"
                  onClick={openDeleteDialog}
                  sx={SECONDARY_DELETE_BUTTON_STYLE}
                >
                  削除
                </Button>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <CancelButton variant="outlined" onClick={handleCancel}>
                    キャンセル
                  </CancelButton>
                  <ActionButton
                    variant="contained"
                    onClick={handleSave}
                    disabled={isLoading}
                    startIcon={
                      isLoading ? (
                        <CircularProgress size={20} color="inherit" />
                      ) : null
                    }
                  >
                    {isLoading ? '保存中...' : '保存'}
                  </ActionButton>
                </Box>
              </Box>
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  mt: 4,
                }}
              >
                <BackButton variant="outlined" onClick={handleBackToList}>
                  一覧へ戻る
                </BackButton>
              </Box>
            )}
          </>
        )}
      </Paper>

      {/* 削除確認ダイアログ */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>医薬品グループの削除</DialogTitle>
        <DialogContent>
          <DialogContentText>
            「{groupName}」を削除しますか？この操作は取り消せません。
            グループに関連するすべてのSOAPテンプレートも削除されます。
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>キャンセル</Button>
          <Button onClick={handleDelete} color="error" autoFocus>
            削除
          </Button>
        </DialogActions>
      </Dialog>

      {/* 一括削除確認ダイアログ */}
      <Dialog
        open={bulkDeleteDialogOpen}
        onClose={() => setBulkDeleteDialogOpen(false)}
      >
        <DialogTitle>選択した医薬品の削除</DialogTitle>
        <DialogContent>
          <DialogContentText>
            選択した{selectedDrugIds.length}
            件の医薬品をグループから削除しますか？ この操作は取り消せません。
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setBulkDeleteDialogOpen(false)}
            variant="outlined"
            color="inherit"
            sx={SECONDARY_DELETE_BUTTON_STYLE}
          >
            キャンセル
          </Button>
          <Button
            onClick={handleBulkDelete}
            color="error"
            variant="outlined"
            autoFocus
          >
            削除
          </Button>
        </DialogActions>
      </Dialog>
    </PortalLayout>
  )
}

export default DrugGroupDetailPage
